/**
 * cartPricing.js
 *
 * Single source of truth for deriving a cart's price breakdown for *display*
 * surfaces (admin Carts list, EditCart "Price Summary"). The intent is that
 * the numbers shown to admin reviewing a cart match exactly what the customer
 * saw at checkout — and what gets persisted to the booking row when admin
 * pushes the cart to Trips.
 *
 * Priority order for every line item:
 *   1. flight_data.pricing_snapshot — saved by Review.jsx the moment the
 *      customer confirmed payment intent. This IS the data the customer saw.
 *   2. calculateReviewPagePricing() with the cart's inputs — reconstructs the
 *      same breakdown for legacy carts that pre-date the snapshot field.
 *
 * IMPORTANT: This helper is for DISPLAY ONLY. The push-to-trips path
 * (handlePushToTrips in EditCart.jsx) has its own pricing block that decides
 * what gets persisted to the bookings row. Keeping the two separate prevents
 * any risk of changing what gets pushed downstream — admins who eyeball the
 * display now just see numbers that *match* the persisted ones, but the
 * persistence itself is unchanged.
 */

import {
  calculateReviewPagePricing,
  calculateServiceFee,
} from '@/utils';
import {
  DEFERA_CAPITAL_HOLD_FEE_RATE,
  SLICE_PAY_HOLD_FEE_RATE,
} from '@/utils/paymentScheduleCalculator';
import { readCartDiscount } from '@/utils/discountCodes';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * @param {object} cart - The AbandonedTrip row.
 * @param {object|null} stay - The StayBooking joined to the cart, if any.
 * @param {object} opts
 * @param {(paymentMethod: string) => boolean} opts.isDeferaCapitalEffective
 *   Resolves whether the active checkout provider is Defera Capital. Passed
 *   in by the caller so the hook context (useInstallmentsCheckoutProvider)
 *   can be inspected without coupling this util to React.
 * @returns {object} Pricing breakdown — see fields below.
 */
export function derivePricingForCart(cart, stay, { isDeferaCapitalEffective }) {
  const flightData = cart?.flight_data || {};
  const snapshot = flightData.pricing_snapshot || null;

  // ---------- Inputs ----------
  const paymentMethod =
    cart?.payment_method ||
    flightData._payment_method ||
    flightData.payment_method ||
    flightData.booking_option ||
    'installments';

  const isMilesOnly = paymentMethod === 'miles';
  const isMilesInstallments = paymentMethod === 'miles_installments';
  const isReserve = paymentMethod === 'reserve';
  const isDeferaZero = paymentMethod === 'defera_zero';
  const promoApplied = !!flightData.promo_applied;

  const originalFlightBase = parseFloat(flightData.total_amount || 0);
  const stayTotal = stay ? parseFloat(stay.totalAmount || stay.total_amount || 0) : 0;
  const isFlightHotel = stayTotal > 0;
  const combinedBase = originalFlightBase + stayTotal;

  const creditsTotal = parseFloat(flightData.credits_total || 0);
  const hasCredits = creditsTotal > 0;

  // CRITICAL: credits-first rule — credits apply ONLY to flight base fare,
  // never to hotel. After the deduction, hotel is added back so fees can
  // calculate against the reduced-flight + full-hotel amount.
  const flightAfterCredits = Math.max(0, originalFlightBase - creditsTotal);
  const feeBase = flightAfterCredits + stayTotal;

  const milesToDeplete = parseFloat(
    cart?.miles_to_deplete ?? flightData._miles_to_deplete ?? 0
  ) || 0;

  const seatTotal = cart?.seat_selections
    ? Object.values(cart.seat_selections).reduce((total, segmentSeats) => {
        return total + Object.values(segmentSeats || {}).reduce((segTotal, seat) => {
          const priceValue =
            typeof seat.price === 'object' && seat.price !== null && 'amount' in seat.price
              ? parseFloat(seat.price.amount)
              : parseFloat(seat.price || 0);
          return segTotal + priceValue;
        }, 0);
      }, 0)
    : 0;

  const isCapital = isDeferaCapitalEffective(paymentMethod);
  const holdRate = isCapital ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;
  const waiveServiceFee = isFlightHotel || promoApplied;

  // ---------- Derive line items ----------
  let serviceFee = 0;
  let holdFee = 0;
  let subtotalBeforeHold = 0;
  let cashPortion = 0;
  let milesUsed = 0;
  let source = 'recalculated';

  // Helper: trust the snapshot for non-miles-installments (where it can't go
  // stale). For miles_installments, the snapshot can be saved before miles
  // were selected — guard with a sanity check that miles_used > 0 and
  // cash_portion < base-after-credits before trusting it.
  const snapshotIsValidForMilesInstallments = !!(
    snapshot &&
    isMilesInstallments &&
    parseFloat(snapshot.miles_used || 0) > 0 &&
    parseFloat(snapshot.cash_portion || 0) < feeBase
  );

  const canTrustSnapshot =
    snapshot &&
    snapshot.grand_total !== undefined &&
    snapshot.grand_total !== null &&
    (!isMilesInstallments || snapshotIsValidForMilesInstallments);

  if (canTrustSnapshot) {
    source = 'snapshot';
    // For flight+hotel/promo, service fee is waived even if the snapshot
    // was taken before that rule was applied — override.
    serviceFee = waiveServiceFee
      ? 0
      : parseFloat(snapshot.service_fee || 0);

    if (isMilesInstallments) {
      cashPortion = parseFloat(snapshot.cash_portion || 0);
      milesUsed = parseFloat(snapshot.miles_used || 0);
      // Recompute service fee on (cash portion + seats) if not waived (this
      // matches calculateReviewPagePricing's miles_installments branch).
      serviceFee = waiveServiceFee
        ? 0
        : calculateServiceFee(cashPortion + seatTotal, isCapital);
      // Seats are financed: part of the held/financed subtotal (matches
      // calculateReviewPagePricing).
      subtotalBeforeHold = cashPortion + serviceFee + seatTotal;
      holdFee = subtotalBeforeHold * holdRate;
    } else if (isFlightHotel) {
      // The snapshot's hold_fee was calculated on the un-waived service fee;
      // recompute it on the now-zero-service-fee subtotal so the displayed
      // numbers reconcile with credits-first + waiver rules.
      const subtotal = feeBase + serviceFee + seatTotal;
      holdFee = subtotal * holdRate;
      subtotalBeforeHold = subtotal;
    } else {
      holdFee = parseFloat(snapshot.hold_fee || 0);
      subtotalBeforeHold = parseFloat(snapshot.grand_total) - holdFee;
    }
  } else if (isMilesInstallments) {
    const pricing = calculateReviewPagePricing(
      feeBase,
      seatTotal,
      paymentMethod,
      milesToDeplete,
      0,
      waiveServiceFee,
      isCapital ? 'defera_capital' : 'slice_pay'
    );
    serviceFee = pricing.serviceFee;
    holdFee = pricing.holdFee;
    cashPortion = pricing.cashPortion;
    milesUsed = pricing.milesUsed;
    subtotalBeforeHold = pricing.subtotalBeforeHold;
  } else if (isReserve) {
    serviceFee = waiveServiceFee ? 0 : (feeBase + seatTotal) * 0.11;
    holdFee = 0;
    subtotalBeforeHold = feeBase + serviceFee;
  } else if (isDeferaZero) {
    const pricing = calculateReviewPagePricing(
      feeBase, seatTotal, 'defera_zero', 0, 0, waiveServiceFee
    );
    serviceFee = pricing.serviceFee;
    holdFee = pricing.holdFee;
    cashPortion = pricing.cashPortion;
    subtotalBeforeHold = pricing.subtotalBeforeHold;
  } else {
    // installments / miles-only / unknown
    // Service fee is calculated on (base + seats), matching calculateReviewPagePricing.
    serviceFee = (isMilesOnly || waiveServiceFee)
      ? 0
      : calculateServiceFee(feeBase + seatTotal, isCapital);
    // Financed paths carry seats inside the held subtotal (matches
    // calculateReviewPagePricing); miles-only adds them flat below.
    const subtotal = feeBase + serviceFee + (isMilesOnly ? 0 : seatTotal);
    holdFee = isMilesOnly ? 0 : subtotal * holdRate;
    subtotalBeforeHold = subtotal;
  }

  // Financed paths (installments / capital / zero / miles_installments) carry
  // seats INSIDE subtotalBeforeHold, so the grand total is simply subtotal +
  // hold. Pay-in-full paths (miles-only, reserve) add seats flat, fee-free.
  const seatsAreFinanced = !isMilesOnly && !isReserve;
  const grandTotalBeforeDiscount = seatsAreFinanced
    ? subtotalBeforeHold + holdFee
    : subtotalBeforeHold + holdFee + seatTotal;

  // Discount code (migration 141) — a flat sum off the grand total, applied
  // last. Read straight from the cart's stamp so this display agrees with what
  // Review charged and with what push-to-trips will persist; admin eyeballing a
  // cart should never see a total the customer didn't.
  const discount = readCartDiscount(flightData);
  const discountApplied = discount
    ? Math.min(discount.cents / 100, grandTotalBeforeDiscount)
    : 0;

  const grandTotalIncludingSeats = round2(grandTotalBeforeDiscount - discountApplied);
  const grandTotalExcludingSeats = round2(
    (seatsAreFinanced
      ? grandTotalBeforeDiscount - seatTotal * (1 + (holdFee > 0 ? holdRate : 0))
      : subtotalBeforeHold + holdFee) - discountApplied
  );

  return {
    // Inputs (echoed for convenience)
    paymentMethod,
    isMilesOnly,
    isMilesInstallments,
    isReserve,
    isDeferaZero,
    isFlightHotel,
    promoApplied,
    hasCredits,
    isCapital,
    discountCode: discount?.code || null,
    discountApplied,

    // Base values
    originalFlightBase,
    combinedBase,
    creditsTotal,
    flightAfterCredits,
    feeBase,
    stayTotal,
    milesToDeplete,

    // Derived line items (always in display dollars, not cents)
    serviceFee,
    holdFee,
    holdRate,
    subtotalBeforeHold,
    cashPortion,
    milesUsed,
    seatTotal,

    // Totals
    grandTotalExcludingSeats,
    grandTotalIncludingSeats,

    // Provenance — useful for debugging "why does the admin see X but the
    // booking row says Y" reports.
    source,
  };
}
